import Papa from 'papaparse';
import React, { useEffect, useMemo, useState } from 'react';
import { auditApi, employeesApi, studentsApi, teachersApi } from '../api';
import {
  Badge,
  DataTable,
  Field,
  HeroPanel,
  MiniBars,
  Panel,
  PortalShell,
  StatCard,
  TextInput,
  Toast,
  getSessionUser
} from '../components/PortalUI';
import { dateOnly, downloadCsv, money, toArray } from '../utils';

const blankStudent = { name: '', gender: '', department: '', year: '', email: '' };
const blankEmployee = { name: '', gender: '', department: '', designation: '', email: '', salary: '' };
const blankTeacher = { name: '', department: '', designation: '', email: '', subjects: '', phone: '' };
const navItems = ['Overview', 'Students', 'Teachers', 'Employees', 'Audit Logs'];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validGenders = ['male', 'female', 'other'];

const validateStudent = (form) => {
  if (!form.name?.trim() || !form.department?.trim() || !form.year?.toString().trim() || !form.email?.trim())
    return 'Please fill in all required details of the student (name, department, year, and email).';
  if (!isValidEmail(form.email))
    return 'Please enter a valid email address (e.g. student@example.com).';
  if (form.gender?.trim() && !validGenders.includes(form.gender.toLowerCase().trim()))
    return 'Gender must be Male, Female, or Other.';
  if (isNaN(Number(form.year)) || Number(form.year) < 1 || Number(form.year) > 6)
    return 'Year must be a number between 1 and 6.';
  return null;
};

const validateEmployee = (form) => {
  if (!form.name?.trim() || !form.department?.trim() || !form.designation?.trim() || !form.email?.trim() || !form.salary?.toString().trim())
    return 'Please fill in all required details of the employee (name, department, designation, email, and salary).';
  if (!isValidEmail(form.email))
    return 'Please enter a valid email address (e.g. employee@example.com).';
  if (form.gender?.trim() && !validGenders.includes(form.gender.toLowerCase().trim()))
    return 'Gender must be Male, Female, or Other.';
  if (isNaN(Number(form.salary)) || Number(form.salary) <= 0)
    return 'Salary must be a valid positive number (e.g. 50000).';
  return null;
};

const validateTeacher = (form) => {
  if (!form.name?.trim() || !form.department?.trim() || !form.designation?.trim() || !form.email?.trim())
    return 'Please fill in all required details of the teacher (name, department, designation, and email).';
  if (!isValidEmail(form.email))
    return 'Please enter a valid email address (e.g. teacher@example.com).';
  return null;
};

async function fetchAllProfiles(api, baseQuery = {}) {
  const limit = 500;
  let page = 1;
  let total = Infinity;
  let all = [];

  while (all.length < total) {
    const data = await api.list({ ...baseQuery, page, limit });
    const items = toArray(data);
    all = all.concat(items);
    total = Number(data?.total ?? all.length);
    if (!items.length || all.length >= total) break;
    page += 1;
  }

  return all;
}

function AdminDashboard() {
  const [active, setActive] = useState('Overview');
  const [students, setStudents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [studentForm, setStudentForm] = useState(blankStudent);
  const [employeeForm, setEmployeeForm] = useState(blankEmployee);
  const [teacherForm, setTeacherForm] = useState(blankTeacher);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [formError, setFormError] = useState('');
  const userId = getSessionUser();

  const load = async () => {
    setLoading(true);
    const [studentData, employeeData, teacherData, logData] = await Promise.all([
      fetchAllProfiles(studentsApi).catch(() => []),
      fetchAllProfiles(employeesApi).catch(() => []),
      fetchAllProfiles(teachersApi).catch(() => []),
      auditApi.list().catch(() => [])
    ]);
    setStudents(toArray(studentData));
    setEmployees(toArray(employeeData));
    setTeachers(toArray(teacherData));
    setAuditLogs(toArray(logData).sort((a, b) => String(b.timestamp || '').localeCompare(String(a.timestamp || ''))));
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filteredStudents = useMemo(() => {
    const term = studentSearch.toLowerCase();
    return students.filter((s) => !term || `${s.studentId} ${s.name} ${s.email} ${s.department}`.toLowerCase().includes(term));
  }, [studentSearch, students]);

  const filteredEmployees = useMemo(() => {
    const term = employeeSearch.toLowerCase();
    return employees.filter((e) => !term || `${e.employeeId} ${e.name} ${e.email} ${e.department} ${e.designation}`.toLowerCase().includes(term));
  }, [employeeSearch, employees]);

  const filteredTeachers = useMemo(() => {
    const term = teacherSearch.toLowerCase();
    return teachers.filter((t) => !term || `${t.teacherId} ${t.name} ${t.email} ${t.department} ${t.designation} ${(t.subjects || []).join?.(' ') || t.subjects}`.toLowerCase().includes(term));
  }, [teacherSearch, teachers]);


  const saveStudent = async () => {
    const error = validateStudent(studentForm);
    if (error) { setFormError(error); return; }
    try {
      setFormError('');
      await studentsApi.create({ ...studentForm, year: Number(studentForm.year || 1) });
      setStudentForm(blankStudent);
      setNotice('Student record created');
      load();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to create student. Please check your details and try again.');
    }
  };

  const saveEmployee = async () => {
    const error = validateEmployee(employeeForm);
    if (error) { setFormError(error); return; }
    try {
      setFormError('');
      await employeesApi.create({ ...employeeForm, salary: Number(employeeForm.salary || 0) });
      setEmployeeForm(blankEmployee);
      setNotice('Employee record created');
      load();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to create employee. Please check your details and try again.');
    }
  };

  const saveTeacher = async () => {
    const error = validateTeacher(teacherForm);
    if (error) { setFormError(error); return; }
    try {
      setFormError('');
      await teachersApi.create(teacherForm);
      setTeacherForm(blankTeacher);
      setNotice('Teacher record created');
      load();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to create teacher. Please check your details and try again.');
    }
  };

  const deleteRecord = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'student') await studentsApi.remove(deleteTarget.id);
    if (deleteTarget.type === 'employee') await employeesApi.remove(deleteTarget.id);
    if (deleteTarget.type === 'teacher') await teachersApi.remove(deleteTarget.id);
    setNotice(`${deleteTarget.name} deleted`);
    setDeleteTarget(null);
    load();
  };

  const importCsv = (type, file) => {
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async ({ data }) => {
        for (const row of data) {
          if (type === 'student') await studentsApi.create({ ...row, year: Number(row.year || 1) });
          if (type === 'employee') await employeesApi.create({ ...row, salary: Number(row.salary || 0) });
          if (type === 'teacher') await teachersApi.create(row);
        }
        setNotice(`${data.length} ${type} rows imported`);
        load();
      }
    });
  };

  const studentColumns = [
    { key: 'studentId', label: 'Student ID', render: (row) => <button onClick={() => setSelectedProfile({ type: 'student', data: row })} className="font-black text-blue-500 dark:text-blue-400">{row.studentId}</button> },
    { key: 'name', label: 'Name', render: (row) => <span className="font-bold text-slate-800 dark:text-white">{row.name}</span> },
    { key: 'department', label: 'Department', render: (row) => <Badge tone="blue">{row.department || '-'}</Badge> },
    { key: 'year', label: 'Year', render: (row) => `Year ${row.year || '-'}` },
    { key: 'email', label: 'Email', render: (row) => <span className="text-slate-500 dark:text-slate-400">{row.email}</span> },
    { key: 'createdAt', label: 'Created', render: (row) => dateOnly(row.createdAt) },
    { key: 'password', label: 'Password', render: (row) => {
      const id = row.studentId;
      return (
        <button
          onClick={() => setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }))}
          className="rounded-lg bg-slate-100 px-3 py-1.5 font-mono text-xs font-black text-slate-700 transition-all duration-150 hover:bg-slate-200 active:scale-[0.97] dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {visiblePasswords[id] ? (row.password || 'N/A') : 'Show'}
        </button>
      );
    } },
    { key: 'actions', label: 'Actions', render: (row) => <button onClick={() => setDeleteTarget({ type: 'student', id: row.studentId, name: row.name })} className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-400 dark:hover:bg-rose-900">Delete</button> }
  ];

  const employeeColumns = [
    { key: 'employeeId', label: 'Employee ID', render: (row) => <button onClick={() => setSelectedProfile({ type: 'employee', data: row })} className="font-black text-emerald-500 dark:text-emerald-400">{row.employeeId}</button> },
    { key: 'name', label: 'Name', render: (row) => <span className="font-bold text-slate-800 dark:text-white">{row.name}</span> },
    { key: 'department', label: 'Department', render: (row) => <Badge tone="emerald">{row.department || '-'}</Badge> },
    { key: 'designation', label: 'Designation' },
    { key: 'salary', label: 'Salary', render: (row) => money(row.salary) },
    { key: 'email', label: 'Email', render: (row) => <span className="text-slate-500 dark:text-slate-400">{row.email}</span> },
    { key: 'password', label: 'Password', render: (row) => {
      const id = row.employeeId;
      return (
        <button
          onClick={() => setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }))}
          className="rounded-lg bg-slate-100 px-3 py-1.5 font-mono text-xs font-black text-slate-700 transition-all duration-150 hover:bg-slate-200 active:scale-[0.97] dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {visiblePasswords[id] ? (row.password || 'N/A') : 'Show'}
        </button>
      );
    } },
    { key: 'actions', label: 'Actions', render: (row) => <button onClick={() => setDeleteTarget({ type: 'employee', id: row.employeeId, name: row.name })} className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-400 dark:hover:bg-rose-900">Delete</button> }
  ];

  const teacherColumns = [
    { key: 'teacherId', label: 'Teacher ID', render: (row) => <button onClick={() => setSelectedProfile({ type: 'teacher', data: row })} className="font-black text-amber-500 dark:text-amber-400">{row.teacherId}</button> },
    { key: 'name', label: 'Name', render: (row) => <span className="font-bold text-slate-800 dark:text-white">{row.name}</span> },
    { key: 'department', label: 'Department', render: (row) => <Badge tone="amber">{row.department || '-'}</Badge> },
    { key: 'designation', label: 'Designation' },
    { key: 'subjects', label: 'Subjects', render: (row) => Array.isArray(row.subjects) ? row.subjects.join(', ') : row.subjects || '-' },
    { key: 'email', label: 'Email', render: (row) => <span className="text-slate-500 dark:text-slate-400">{row.email}</span> },
    { key: 'password', label: 'Password', render: (row) => {
      const id = row.teacherId;
      return (
        <button
          onClick={() => setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }))}
          className="rounded-lg bg-slate-100 px-3 py-1.5 font-mono text-xs font-black text-slate-700 transition-all duration-150 hover:bg-slate-200 active:scale-[0.97] dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {visiblePasswords[id] ? (row.password || 'N/A') : 'Show'}
        </button>
      );
    } },
    { key: 'actions', label: 'Actions', render: (row) => <button onClick={() => setDeleteTarget({ type: 'teacher', id: row.teacherId, name: row.name })} className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-400 dark:hover:bg-rose-900">Delete</button> }
  ];

  const auditColumns = [
    { key: 'timestamp', label: 'Timestamp', render: (row) => row.timestamp ? row.timestamp.replace('T', ' ').split('.')[0] : '-' },
    { key: 'userId', label: 'User' },
    { key: 'action', label: 'Action', render: (row) => <Badge tone="violet">{row.action}</Badge> },
    { key: 'targetId', label: 'Target' },
    { key: 'result', label: 'Result', render: (row) => <Badge tone={row.result === 'SUCCESS' ? 'emerald' : 'rose'}>{row.result || 'SUCCESS'}</Badge> }
  ];

  const renderStudentForm = (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        {Object.keys(blankStudent).map((key) => (
          <Field key={key} label={key}>
            <TextInput value={studentForm[key]} onChange={(e) => { setStudentForm({ ...studentForm, [key]: e.target.value }); setFormError(''); }} placeholder={key} className="focus:ring-blue-100 focus:border-blue-500" />
          </Field>
        ))}
        <button onClick={saveStudent} className="self-end rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-black text-white hover:bg-blue-700">Add Student</button>
      </div>
      {formError && active === 'Students' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">⚠️ {formError}</div>}
    </div>
  );

  const renderEmployeeForm = (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-7">
        {Object.keys(blankEmployee).map((key) => (
          <Field key={key} label={key}>
            <TextInput value={employeeForm[key]} onChange={(e) => { setEmployeeForm({ ...employeeForm, [key]: e.target.value }); setFormError(''); }} placeholder={key} className="focus:ring-emerald-100 focus:border-emerald-500" />
          </Field>
        ))}
        <button onClick={saveEmployee} className="self-end rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-700">Add Employee</button>
      </div>
      {formError && active === 'Employees' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">⚠️ {formError}</div>}
    </div>
  );

  const renderTeacherForm = (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-7">
        {Object.keys(blankTeacher).map((key) => (
          <Field key={key} label={key}>
            <TextInput value={teacherForm[key]} onChange={(e) => { setTeacherForm({ ...teacherForm, [key]: e.target.value }); setFormError(''); }} placeholder={key === 'subjects' ? 'DBMS, Cloud Computing' : key} className="focus:ring-amber-100 focus:border-amber-500" />
          </Field>
        ))}
        <button onClick={saveTeacher} className="self-end rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-black text-white hover:bg-amber-600">Add Teacher</button>
      </div>
      {formError && active === 'Teachers' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">⚠️ {formError}</div>}
    </div>
  );

  return (
    <PortalShell
      role="admin"
      title={active}
      subtitle="Manage student records, employees, bulk imports, audit trails and operational health from one command center."
      navItems={navItems}
      active={active}
      onNavChange={(tab) => { setActive(tab); setFormError(''); }}
      actions={<button onClick={load} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Refresh</button>}
    >
      <div className="space-y-6">
        <HeroPanel role="admin" eyebrow="Signed in as administrator" title={`Welcome back, ${userId}`}>
          Your CloudRecords control layer is ready. Review record health, import data, inspect audit trails and keep institutional information clean.
        </HeroPanel>

        {notice && <Toast message={notice} onClose={() => setNotice('')} />}

        {active === 'Overview' && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger">
              <StatCard label="Total Students" value={students.length} tone="text-blue-600" detail="Academic profiles" />
              <StatCard label="Total Teachers" value={teachers.length} tone="text-amber-600" detail="Faculty profiles" />
              <StatCard label="Total Employees" value={employees.length} tone="text-emerald-600" detail="Staff profiles" />
              <StatCard label="Audit Events" value={auditLogs.length} tone="text-violet-600" detail="System activity" />
            </div>
            <div className="grid gap-5 xl:grid-cols-2">
              <Panel title="Student Departments" subtitle="Department-wise student distribution.">
                <MiniBars data={students.reduce((acc, item) => ({ ...acc, [item.department || 'Unknown']: (acc[item.department || 'Unknown'] || 0) + 1 }), {})} color="bg-blue-500" />
              </Panel>
              <Panel title="Employee Departments" subtitle="Department-wise employee and teacher distribution.">
                <MiniBars data={[...employees, ...teachers].reduce((acc, item) => ({ ...acc, [item.department || 'Unknown']: (acc[item.department || 'Unknown'] || 0) + 1 }), {})} color="bg-emerald-500" />
              </Panel>
              <Panel title="Recent Audit Actions" subtitle="Most common activity types.">
                <MiniBars data={auditLogs.reduce((acc, item) => ({ ...acc, [item.action || 'UNKNOWN']: (acc[item.action || 'UNKNOWN'] || 0) + 1 }), {})} color="bg-violet-500" />
              </Panel>
              <Panel title="Quick Actions" subtitle="Common administrative tasks.">
                <div className="grid gap-3">
                  <button onClick={() => setActive('Students')} className="rounded-lg bg-blue-50 px-4 py-3 text-left text-sm font-black text-blue-700 hover:bg-blue-100">Manage students</button>
                  <button onClick={() => setActive('Teachers')} className="rounded-lg bg-amber-50 px-4 py-3 text-left text-sm font-black text-amber-700 hover:bg-amber-100">Manage teachers</button>
                  <button onClick={() => setActive('Employees')} className="rounded-lg bg-emerald-50 px-4 py-3 text-left text-sm font-black text-emerald-700 hover:bg-emerald-100">Manage employees</button>
                  <button onClick={() => setActive('Audit Logs')} className="rounded-lg bg-violet-50 px-4 py-3 text-left text-sm font-black text-violet-700 hover:bg-violet-100">Review audit logs</button>
                </div>
              </Panel>
            </div>
          </>
        )}

        {active === 'Students' && (
          <Panel
            title="Students"
            subtitle={`${filteredStudents.length} of ${students.length} records visible`}
            actions={
              <>
                <label className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-black text-white hover:bg-slate-800">
                  Import CSV
                  <input type="file" accept=".csv" className="hidden" onChange={(e) => importCsv('student', e.target.files?.[0])} />
                </label>
                <button onClick={() => downloadCsv('students.csv', students)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-black text-white hover:bg-blue-700">Export</button>
              </>
            }
          >
            <div className="mb-5 grid gap-4">
              <TextInput value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} placeholder="Search by name, ID, email or department..." className="focus:ring-blue-100 focus:border-blue-500" />
              {renderStudentForm}
            </div>
            <DataTable columns={studentColumns} rows={filteredStudents} loading={loading} rowKey={(row) => row.studentId} />
          </Panel>
        )}

        {active === 'Teachers' && (
          <Panel
            title="Teachers"
            subtitle={`${filteredTeachers.length} of ${teachers.length} records visible`}
            actions={
              <>
                <label className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-black text-white hover:bg-slate-800">
                  Import CSV
                  <input type="file" accept=".csv" className="hidden" onChange={(e) => importCsv('teacher', e.target.files?.[0])} />
                </label>
                <button onClick={() => downloadCsv('teachers.csv', teachers)} className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-black text-white hover:bg-amber-600">Export</button>
              </>
            }
          >
            <div className="mb-5 grid gap-4">
              <TextInput value={teacherSearch} onChange={(e) => setTeacherSearch(e.target.value)} placeholder="Search by name, ID, email, department, designation or subject..." className="focus:ring-amber-100 focus:border-amber-500" />
              {renderTeacherForm}
            </div>
            <DataTable columns={teacherColumns} rows={filteredTeachers} loading={loading} rowKey={(row) => row.teacherId} />
          </Panel>
        )}

        {active === 'Employees' && (
          <Panel
            title="Employees"
            subtitle={`${filteredEmployees.length} of ${employees.length} records visible`}
            actions={
              <>
                <label className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-black text-white hover:bg-slate-800">
                  Import CSV
                  <input type="file" accept=".csv" className="hidden" onChange={(e) => importCsv('employee', e.target.files?.[0])} />
                </label>
                <button onClick={() => downloadCsv('employees.csv', employees)} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white hover:bg-emerald-700">Export</button>
              </>
            }
          >
            <div className="mb-5 grid gap-4">
              <TextInput value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)} placeholder="Search by name, ID, email, department or designation..." className="focus:ring-emerald-100 focus:border-emerald-500" />
              {renderEmployeeForm}
            </div>
            <DataTable columns={employeeColumns} rows={filteredEmployees} loading={loading} rowKey={(row) => row.employeeId} />
          </Panel>
        )}

        {active === 'Audit Logs' && (
          <Panel title="Audit Logs" subtitle="A chronological view of CloudRecords activity." actions={<button onClick={() => downloadCsv('auditlogs.csv', auditLogs)} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-black text-white hover:bg-violet-700">Export</button>}>
            <DataTable columns={auditColumns} rows={auditLogs} loading={loading} />
          </Panel>
        )}
      </div>

      {selectedProfile && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4" onClick={() => setSelectedProfile(null)}>
          <div className="w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-2xl animate-float-in dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
            <div className={`bg-gradient-to-br ${selectedProfile.type === 'student' ? 'from-blue-600 to-cyan-500' : selectedProfile.type === 'teacher' ? 'from-amber-500 to-orange-500' : 'from-emerald-600 to-teal-500'} p-6 text-white`}>
              <p className="text-xs font-black uppercase tracking-wide text-white/70">{selectedProfile.type} profile</p>
              <h3 className="mt-2 text-2xl font-black">{selectedProfile.data.name}</h3>
              <p className="text-white/80">{selectedProfile.data.studentId || selectedProfile.data.teacherId || selectedProfile.data.employeeId}</p>
            </div>
            <div className="grid gap-3 p-5 sm:grid-cols-2">
              {Object.entries(selectedProfile.data).filter(([key]) => !['recordType', 'before', 'after', 'password'].includes(key)).map(([key, value]) => (
                <div key={key} className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                  <p className="text-xs font-black uppercase text-slate-400">{key}</p>
                  <p className="mt-1 break-words text-sm font-bold text-slate-800 dark:text-slate-100">{String(value || '-')}</p>
                </div>
              ))}
              <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                <p className="text-xs font-black uppercase text-slate-400">Login Password</p>
                <button
                  onClick={() => {
                    const id = selectedProfile.data.studentId || selectedProfile.data.teacherId || selectedProfile.data.employeeId;
                    setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }));
                  }}
                  className="mt-1 font-mono text-sm font-black text-slate-800 transition-all duration-150 active:scale-[0.97] dark:text-slate-100"
                >
                  {visiblePasswords[selectedProfile.data.studentId || selectedProfile.data.teacherId || selectedProfile.data.employeeId] ? (selectedProfile.data.password || 'N/A') : 'Show password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl animate-float-in dark:bg-slate-900">
            <h3 className="text-xl font-black text-slate-950">Delete record?</h3>
            <p className="mt-2 text-sm text-slate-500">This will permanently delete {deleteTarget.name}. This action cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-black text-slate-700">Cancel</button>
              <button onClick={deleteRecord} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-black text-white">Delete</button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}

export default AdminDashboard;

